import React, { useState, useEffect } from "react";
import { 
  Download, 
  Smartphone, 
  Globe, 
  X, 
  Check, 
  QrCode, 
  Share2, 
  Sparkles, 
  ShieldCheck,
  ExternalLink,
  Copy,
  Layers,
  Store
} from "lucide-react";
import { motion } from "motion/react";

interface AppDownloadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onToast: (msg: string, type?: "success" | "error") => void;
}

export default function AppDownloadModal({
  isOpen,
  onClose,
  onToast,
}: AppDownloadModalProps) {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [downloadingManifest, setDownloadingManifest] = useState(false);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    };
  }, []);

  if (!isOpen) return null;

  const appUrl = window.location.href;

  const handleInstallPWA = async () => {
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === "accepted") {
          onToast("App instalado com sucesso na tela inicial!", "success");
          setDeferredPrompt(null);
          return;
        }
      } catch (err) {
        console.warn("PWA prompt error:", err);
      }
    }
    
    // Se estiver em iframe ou sem evento nativo, abre a aplicação em uma nova aba completa para permitir instalação direta
    try {
      window.open(appUrl, "_blank");
      onToast("Abrindo o App em nova aba! No menu do navegador (⋮ ou ↑) clique em 'Instalar aplicativo' ou 'Adicionar à Tela Inicial'.", "success");
    } catch (e) {
      onToast("Abra o menu do seu navegador (⋮ ou ↑) e selecione 'Adicionar à Tela Inicial'.", "success");
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(appUrl);
    setCopiedLink(true);
    onToast("Link do aplicativo copiado com sucesso!", "success");
    setTimeout(() => setCopiedLink(false), 3000);
  };

  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur-md z-50 flex items-center justify-center p-4 overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 15 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 15 }}
        className="bg-zinc-950 border border-zinc-800 rounded-3xl p-6 sm:p-8 max-w-lg w-full text-white shadow-2xl space-y-6 relative"
      >
        {/* BOTÃO FECHAR */}
        <button
          onClick={onClose}
          className="absolute top-5 right-5 p-2 rounded-full bg-zinc-900 text-zinc-400 hover:text-white transition cursor-pointer"
        >
          <X size={18} />
        </button>

        {/* CABEÇALHO */}
        <div className="text-center space-y-2 pt-2">
          <div className="w-14 h-14 bg-gradient-to-tr from-emerald-500 to-amber-400 text-zinc-950 rounded-2xl flex items-center justify-center mx-auto font-black shadow-lg">
            <Smartphone size={28} />
          </div>
          <h2 className="text-2xl font-display font-black text-white">
            Instalar Aplicativo no Celular
          </h2>
          <p className="text-xs text-zinc-400 max-w-md mx-auto">
            Adicione o app direto na tela inicial do seu celular (Android ou iPhone) para acesso rápido sem precisar de loja.
          </p>
        </div>

        {/* OPÇÕES PRINCIPAIS */}
        <div className="space-y-4">
          
          {/* CARTÃO 1: INSTALAÇÃO RÁPIDA PWA */}
          <div className="bg-emerald-950/40 border border-emerald-800/60 p-5 rounded-2xl space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/20 text-emerald-400 flex items-center justify-center font-bold shrink-0">
                <Download size={20} />
              </div>
              <div>
                <h4 className="font-bold text-sm text-white flex items-center gap-2">
                  <span>Instalar Aplicativo (PWA)</span>
                  <span className="text-[9px] bg-emerald-500 text-zinc-950 px-2 py-0.5 rounded-full font-black uppercase">
                    Direto no Celular
                  </span>
                </h4>
                <p className="text-[11px] text-zinc-400 mt-0.5">
                  Clique no botão abaixo para disparar o instalador ou siga os passos do seu navegador.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={handleInstallPWA}
              className="w-full py-3 px-4 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-black text-sm rounded-xl shadow-lg transition flex items-center justify-center gap-2 cursor-pointer active:scale-98"
            >
              <Smartphone size={18} />
              <span>Instalar Agora na Tela Inicial</span>
            </button>
          </div>

          {/* PASSO A PASSO ILUSTRADO POR SISTEMA */}
          <div className="bg-zinc-900/60 border border-zinc-800 p-4 rounded-2xl space-y-3">
            <span className="text-xs font-black text-amber-400 uppercase tracking-wider block">
              📲 Como Instalar Manualmente em 1 Clique:
            </span>

            <div className="space-y-2.5 text-xs">
              <div className="bg-zinc-950 p-3 rounded-xl border border-zinc-800/80 space-y-1">
                <strong className="text-emerald-400 font-bold block flex items-center gap-1.5">
                  <span>🤖 No Android (Google Chrome):</span>
                </strong>
                <p className="text-zinc-300 text-[11px] leading-relaxed">
                  1. Abra o menu do Chrome tocando nos <strong>três pontinhos (⋮)</strong> no canto superior direito.<br />
                  2. Toque em <strong>"Instalar aplicativo"</strong> ou <strong>"Adicionar à Tela inicial"</strong>.
                </p>
              </div>

              <div className="bg-zinc-950 p-3 rounded-xl border border-zinc-800/80 space-y-1">
                <strong className="text-amber-300 font-bold block flex items-center gap-1.5">
                  <span>🍎 No iPhone / iPad (Safari):</span>
                </strong>
                <p className="text-zinc-300 text-[11px] leading-relaxed">
                  1. Toque no botão de <strong>Compartilhar (↑)</strong> na barra inferior do Safari.<br />
                  2. Role a lista para baixo e toque em <strong>"Adicionar à Tela de Início"</strong>.
                </p>
              </div>
            </div>
          </div>

          {/* CARTÃO COMPARTILHAR LINK */}
          <div className="bg-zinc-900/40 border border-zinc-850 p-3.5 rounded-2xl flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <span className="text-[10px] font-bold text-zinc-500 uppercase block">Link Direto do App</span>
              <span className="text-xs font-mono text-zinc-300 truncate block">{appUrl}</span>
            </div>
            <button
              type="button"
              onClick={handleCopyLink}
              className="py-2 px-3 bg-zinc-800 hover:bg-zinc-700 text-white text-xs font-bold rounded-xl transition flex items-center gap-1.5 shrink-0 cursor-pointer"
            >
              {copiedLink ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
              <span>{copiedLink ? "Copiado!" : "Copiar Link"}</span>
            </button>
          </div>

        </div>
      </motion.div>
    </div>
  );
}
